import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Sparkles, FileDown, RefreshCw, Loader2, ExternalLink, Presentation, Tag, Repeat2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { META_MARKETS, META_MARKET_FLAGS } from './MetaStatsPage';
import { MetaChannelBadge } from '../components/MetaChannelBadge';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 300000;

const CATEGORY_OPTIONS = [
  { value: 'produkt',    label: 'Produkt' },
  { value: 'tavling',    label: 'Tävling' },
  { value: 'gemenskap',  label: 'Gemenskap' },
  { value: 'kampanj',    label: 'Kampanj' },
  { value: 'sasong',     label: 'Säsong' },
];

async function pollJob(statusUrl, accessToken) {
  const startedAt = Date.now();
  while (Date.now() - startedAt < POLL_TIMEOUT_MS) {
    const r = await fetch(statusUrl, { headers: { Authorization: `Bearer ${accessToken}` } });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const job = await r.json();
    if (job.status === 'done') return job.result;
    if (job.status === 'error') throw new Error(job.error || 'Okänt fel');
    await new Promise(res => setTimeout(res, POLL_INTERVAL_MS));
  }
  throw new Error('Tidsgränsen överskreds — försök igen.');
}

function renderInline(text, keyPrefix) {
  // Konverterar **fet text** till <strong> inom ett textstycke/listpunkt.
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    const m = part.match(/^\*\*([^*]+)\*\*$/);
    return m ? <strong key={`${keyPrefix}-b-${i}`}>{m[1]}</strong> : <React.Fragment key={`${keyPrefix}-t-${i}`}>{part}</React.Fragment>;
  });
}

function renderAnalysisText(text) {
  if (!text) return null;
  const lines = text.split('\n');
  const blocks = [];
  let listBuffer = [];

  const flushList = (key) => {
    if (listBuffer.length > 0) {
      blocks.push(<ul className="mstat-insights-list" key={`ul-${key}`}>{listBuffer.map((li, i) => <li key={i}>{renderInline(li, `li-${key}-${i}`)}</li>)}</ul>);
      listBuffer = [];
    }
  };

  lines.forEach((line, idx) => {
    const trimmed = line.trim();
    if (trimmed.startsWith('## ')) {
      flushList(idx);
      blocks.push(<h3 className="mstat-insights-heading" key={idx}>{trimmed.replace(/^##\s*/, '')}</h3>);
    } else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      listBuffer.push(trimmed.replace(/^[-*]\s*/, ''));
    } else if (trimmed) {
      flushList(idx);
      blocks.push(<p className="mstat-insights-p" key={idx}>{renderInline(trimmed, `p-${idx}`)}</p>);
    }
  });
  flushList('end');
  return blocks;
}

function PostCategoryList({ posts, accessToken, onUpdated }) {
  const [savingKey, setSavingKey] = useState(null);
  const [errorByKey, setErrorByKey] = useState({});

  const handleChange = useCallback(async (post, newCategory) => {
    const key = `${post.platform}:${post.id}`;
    setSavingKey(key);
    setErrorByKey(prev => ({ ...prev, [key]: null }));
    try {
      const r = await fetch(`${API_URL}/api/meta-stats/insights/categories`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ platform: post.platform, post_id: post.id, category: newCategory }),
      });
      if (!r.ok) {
        const errBody = await r.json().catch(() => ({}));
        throw new Error(errBody.detail || `HTTP ${r.status}`);
      }
      onUpdated(post, newCategory);
    } catch (e) {
      setErrorByKey(prev => ({ ...prev, [key]: e.message || 'Kunde inte spara' }));
    } finally {
      setSavingKey(null);
    }
  }, [accessToken, onUpdated]);

  if (!posts?.length) return null;

  return (
    <div className="mstat-cat-wrap" data-testid="metastats-insights-category-list">
      <div className="mstat-cat-header">
        <Tag size={13} /> Inlägg i perioden — kategori per inlägg
        <span className="mstat-cat-header-note">Ändra vid behov — sparas permanent för just det inlägget.</span>
      </div>
      <div className="mstat-cat-rows">
        {posts.map(p => {
          const key = `${p.platform}:${p.id}`;
          const isManual = p.category_source === 'manual';
          return (
            <div className="mstat-cat-row" key={key} data-testid={`metastats-cat-row-${key}`}>
              <span className="mstat-cat-platform">{p.platform === 'instagram' ? 'IG' : 'FB'}</span>
              <span className="mstat-cat-date">{(p.date || '').slice(0, 10)}</span>
              <span className="mstat-cat-text" title={p.text_preview}>{p.text_preview || '(ingen text)'}</span>
              {p.republished && (
                <span className="mstat-cat-republished" data-testid={`metastats-cat-republished-${key}`}>
                  <Repeat2 size={11} /> Återpublicerat
                </span>
              )}
              <select
                className={`mstat-cat-select${isManual ? ' mstat-cat-select--manual' : ''}`}
                data-testid={`metastats-cat-select-${key}`}
                value={p.category || ''}
                disabled={savingKey === key}
                onChange={(e) => handleChange(p, e.target.value)}
              >
                <option value="" disabled>Välj kategori</option>
                {CATEGORY_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              {p.category && !isManual && p.category_confidence != null && (
                <span className="mstat-cat-confidence">{Math.round(p.category_confidence)}% AI</span>
              )}
              {isManual && <span className="mstat-cat-confidence mstat-cat-confidence--manual">Manuell</span>}
              {errorByKey[key] && <span className="mstat-cat-error" data-testid="metastats-cat-error" title={errorByKey[key]}>Kunde inte spara</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function MetaStatsInsightsTab() {
  const { session } = useAuth();
  const { addUsageLog } = useData();
  const accessToken = session?.access_token;

  const [market, setMarket] = useState('SE');
  const marketRef = useRef(market);
  useEffect(() => { marketRef.current = market; }, [market]);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState(null);

  const [presentationHtml, setPresentationHtml] = useState(null);
  const [buildingPresentation, setBuildingPresentation] = useState(false);
  const [presentationError, setPresentationError] = useState(null);
  const blobUrlRef = useRef(null);

  const revokeBlobUrl = () => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  };

  useEffect(() => () => revokeBlobUrl(), []);

  const handleGenerate = useCallback(async () => {
    const requestedMarket = market;
    setAnalyzing(true);
    setAnalyzeError(null);
    setAnalysis(null);
    setPresentationHtml(null);
    revokeBlobUrl();
    try {
      const startRes = await fetch(`${API_URL}/api/meta-stats/insights/generate/start`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ market: requestedMarket }),
      });
      if (!startRes.ok) {
        const errBody = await startRes.json().catch(() => ({}));
        throw new Error(errBody.detail || `HTTP ${startRes.status}`);
      }
      const { job_id } = await startRes.json();
      const result = await pollJob(`${API_URL}/api/meta-stats/insights/generate/status/${job_id}`, accessToken);
      if (requestedMarket !== marketRef.current) return; // användaren bytte marknad medan jobbet kördes — kasta resultatet
      setAnalysis(result);
      if (result.usage) {
        addUsageLog({
          model: result.usage.model, inputTokens: result.usage.inputTokens, outputTokens: result.usage.outputTokens,
          market: requestedMarket, category: 'meta_insights', source: 'meta_stats', module: 'meta_insights',
        });
      }
    } catch (e) {
      if (requestedMarket === marketRef.current) setAnalyzeError(e.message || 'Kunde inte generera analysen.');
    } finally {
      if (requestedMarket === marketRef.current) setAnalyzing(false);
    }
  }, [accessToken, market, addUsageLog]);

  const handleBuildPresentation = useCallback(async () => {
    if (!analysis?.analysis_text) return;
    const requestedMarket = market;
    setBuildingPresentation(true);
    setPresentationError(null);
    revokeBlobUrl();
    setPresentationHtml(null);
    try {
      const startRes = await fetch(`${API_URL}/api/meta-stats/insights/presentation/start`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ market: requestedMarket, analysis_text: analysis.analysis_text }),
      });
      if (!startRes.ok) {
        const errBody = await startRes.json().catch(() => ({}));
        throw new Error(errBody.detail || `HTTP ${startRes.status}`);
      }
      const { job_id } = await startRes.json();
      const result = await pollJob(`${API_URL}/api/meta-stats/insights/presentation/status/${job_id}`, accessToken);
      if (requestedMarket !== marketRef.current) return; // användaren bytte marknad medan jobbet kördes — kasta resultatet
      setPresentationHtml(result.html);
      if (result.usage) {
        addUsageLog({
          model: result.usage.model, inputTokens: result.usage.inputTokens, outputTokens: result.usage.outputTokens,
          market: requestedMarket, category: 'meta_insights_presentation', source: 'meta_stats', module: 'meta_insights',
        });
      }
    } catch (e) {
      if (requestedMarket === marketRef.current) setPresentationError(e.message || 'Kunde inte skapa presentationen.');
    } finally {
      if (requestedMarket === marketRef.current) setBuildingPresentation(false);
    }
  }, [accessToken, market, analysis, addUsageLog]);

  const getBlobUrl = () => {
    if (blobUrlRef.current) return blobUrlRef.current;
    const blob = new Blob([presentationHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    blobUrlRef.current = url;
    return url;
  };

  const handleDownload = () => {
    const url = getBlobUrl();
    const a = document.createElement('a');
    a.href = url;
    a.download = `meta-insikter-${market}-${new Date().toISOString().slice(0, 10)}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handlePreview = () => window.open(getBlobUrl(), '_blank');

  const handleMarketChange = (m) => {
    if (m === market || analyzing || buildingPresentation) return;
    setMarket(m);
    // Nollställ gammal analys/presentation så den aldrig kan skickas vidare för fel marknad.
    setAnalysis(null);
    setAnalyzeError(null);
    setPresentationHtml(null);
    setPresentationError(null);
    revokeBlobUrl();
  };

  const showDataGapFallback = analysis?.analysis_text && !analysis.analysis_text.includes('Begränsningar');

  return (
    <div data-testid="metastats-insights-tab">
      <div className="mstat-tab-badge-row">
        <MetaChannelBadge type="organic" label="AI-driven organisk analys" />
        <span className="mstat-ai-signature">Drivs av Gemini 2.5 &amp; Claude Sonnet 4.6</span>
      </div>
      <p className="mstat-note" style={{ marginBottom: 16 }}>
        AI-genererad analys av organiska Facebook- och Instagram-inlägg för EN marknad, senaste 30 dagarna.
        Välj marknad och klicka på "Generera analys".
      </p>

      <div className="mstat-market-filter" data-testid="metastats-insights-market-select" style={{ marginBottom: 16 }}>
        {META_MARKETS.map(m => (
          <button
            key={m}
            type="button"
            className={`mstat-market-chip${market === m ? ' mstat-market-chip--selected' : ''}`}
            data-testid={`metastats-insights-market-${m}`}
            disabled={analyzing || buildingPresentation}
            onClick={() => handleMarketChange(m)}
          >
            <span className="mstat-market-chip-flag">{META_MARKET_FLAGS[m]}</span>
            <span className="mstat-market-chip-code">{m}</span>
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <button
          className="tab-btn tab-btn--active"
          data-testid="metastats-insights-generate-btn"
          disabled={analyzing}
          onClick={handleGenerate}
        >
          {analyzing ? <Loader2 size={14} className="mstat-spin" /> : <Sparkles size={14} />}
          {analyzing ? 'Analyserar...' : 'Generera analys'}
        </button>
        {analysis && !analyzing && (
          <button className="tab-btn" data-testid="metastats-insights-regenerate-btn" onClick={handleGenerate}>
            <RefreshCw size={14} /> Regenerera
          </button>
        )}
      </div>

      {analyzing && (
        <div className="admin-loading" data-testid="metastats-insights-loading">
          Analyserar organiska inlägg för {market} (senaste 30 dagarna) — detta kan ta upp till en minut...
        </div>
      )}

      {analyzeError && (
        <div className="admin-notice" data-testid="metastats-insights-error">⚠ {analyzeError}</div>
      )}

      {analysis && !analyzing && (
        <>
          <div className="mstat-insights-meta" data-testid="metastats-insights-postcount">
            Baserat på {analysis.post_count} organiska inlägg för {META_MARKET_FLAGS[market]} {market} (senaste 30 dagarna).
          </div>

          {analysis.posts && (
            <PostCategoryList
              posts={analysis.posts}
              accessToken={accessToken}
              onUpdated={(post, newCategory) => {
                setAnalysis(prev => ({
                  ...prev,
                  posts: prev.posts.map(p => (p.platform === post.platform && p.id === post.id)
                    ? { ...p, category: newCategory, category_source: 'manual', category_confidence: null }
                    : p),
                }));
              }}
            />
          )}

          <div className="mstat-insights-content" data-testid="metastats-insights-text">
            {renderAnalysisText(analysis.analysis_text)}
          </div>

          {showDataGapFallback && (
            <p className="mstat-note mstat-datagap-note" data-testid="metastats-insights-datagap-fallback">{analysis.data_gap_note}</p>
          )}

          <div className="admin-section">
            <h2 className="admin-section-title">Presentation</h2>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                className="tab-btn tab-btn--active"
                data-testid="metastats-insights-build-presentation-btn"
                disabled={buildingPresentation}
                onClick={handleBuildPresentation}
              >
                {buildingPresentation ? <Loader2 size={14} className="mstat-spin" /> : <Presentation size={14} />}
                {buildingPresentation ? 'Skapar presentation...' : 'Skapa en presentation'}
              </button>
              {presentationHtml && (
                <>
                  <button className="tab-btn" data-testid="metastats-insights-preview-btn" onClick={handlePreview}>
                    <ExternalLink size={14} /> Förhandsgranska
                  </button>
                  <button className="tab-btn" data-testid="metastats-insights-download-btn" onClick={handleDownload}>
                    <FileDown size={14} /> Ladda ner presentation
                  </button>
                </>
              )}
            </div>
            {buildingPresentation && (
              <div className="admin-loading" style={{ marginTop: 12 }} data-testid="metastats-insights-presentation-loading">
                Bygger HTML-presentation med diagram — detta kan ta 2–3 minuter...
              </div>
            )}
            {presentationError && (
              <div className="admin-notice" style={{ marginTop: 12 }} data-testid="metastats-insights-presentation-error">⚠ {presentationError}</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
